import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { formatNumber } from '@engine/utils/format'
import Checkbox from '@material-ui/core/Checkbox'
import TextField from '@material-ui/core/TextField'
import { Block, Delete, Done, DragIndicator, Edit } from '@material-ui/icons'
import './DataTableRow.scss'

const DataTableRow = props => {
  const [localRow, setLocalRow] = useState(props.row)
  const [bedVal, bathVal] = formatNumber('unitType', localRow.unitType).split(
    '/'
  )
  const [floorPlan, setFloorPlan] = useState({ bed: bedVal, bath: bathVal })

  useEffect(
    () => {
      setLocalRow(props.row)
    },
    [props.row]
  )
  const {
    editRowIndex,
    index,
    rowsCount,
    selectedRows,
    isLastRow,
    minimumOneRow,
    dataAttributes,
    onDragOver,
    onDragStart,
    onDrop,
    onSelect,
    onEdit,
    onDelete,
    onUpdate,
    actionItems,
    newRowFlag,
    confirmAddRow,
    cancelAddRow
  } = props
  const { hasDelete, hasDragDrop, hasEdit, hasSelect } = actionItems
    ? actionItems
    : {}

  const handleChange = event => {
    onSelect(event.target.value)
  }

  const handleUpdate = () => {
    const { units, unitType, unitSize, unitEffectiveRent, erPerSf } = localRow
    const rowHasData =
      units || unitType || unitSize || unitEffectiveRent || erPerSf
    if (rowHasData) {
      if (newRowFlag) {
        confirmAddRow(localRow)
      } else {
        onUpdate(localRow, index)
        onEdit(-1)
      }
    } else {
      props.onEmptyRow(true)
    }
  }

  const handleTextFieldChange = event => {
    const { name, value } = event.target
    setLocalRow({
      ...localRow,
      [name]: name === 'comments' ? value : Number(value)
    })
    props.onEmptyRow(false)
  }

  const handleUnitTypeChange = event => {
    const { name, value } = event.target
    const fpObj = { ...floorPlan }
    if (name.includes('bed')) {
      fpObj.bed = value
      setFloorPlan({ ...fpObj })
      const { bath } = floorPlan
      setLocalRow({
        ...localRow,
        // eslint-disable-next-line
        ['unitType']: `${value} Bedroom / ${bath} Bath`
      })
    } else if (name.includes('bath')) {
      fpObj.bath = value
      setFloorPlan({ ...fpObj })
      const { bed } = floorPlan
      setLocalRow({
        ...localRow,
        // eslint-disable-next-line
        ['unitType']: `${bed} Bedroom / ${value} Bath`
      })
    }
  }

  const handleBlock = () => {
    if (newRowFlag) {
      cancelAddRow()
    } else {
      setLocalRow({
        ...props.row
      })
      onEdit(-1)
    }
  }

  const handleSetEdit = i => {
    onEdit(i)
  }

  const getEditableRow = key => {
    if (key !== 'unitType') {
      return (
        <TextField
          variant="outlined"
          size="small"
          margin="dense"
          name={key}
          type={key === 'comments' ? 'string' : 'number'}
          value={localRow[key]}
          InputProps={{ 'data-testid': `column-${key}` }}
          className={`text-field ${key === 'comments' ? 'comments' : ''}`}
          onChange={handleTextFieldChange}
          autoFocus={key === 'units'}
          disabled={key === 'mrPerSf' || key === 'erPerSf' || key === 'erPerBed' || key === 'unitEffectiveRent'}
        />
      )
    }

    return (
      <div className="unit-type">
        <TextField
          variant="outlined"
          size="small"
          margin="dense"
          name={`${key}-bed`}
          type="number"
          value={floorPlan.bed}
          InputProps={{
            'data-testid': `column-bed-${key}`,
            inputProps: { min: 0, max: 8 }
          }}
          className="sm-text-field"
          onChange={handleUnitTypeChange}
        />
        /
        <TextField
          variant="outlined"
          size="small"
          margin="dense"
          name={`${key}-bath`}
          type="number"
          value={floorPlan.bath}
          InputProps={{
            'data-testid': `column-bath-${key}`,
            inputProps: { min: 1, max: 6, step: 0.5 }
          }}
          className="sm-text-field"
          onChange={handleUnitTypeChange}
        />
      </div>
    )
  }

  return (
    <div
      data-testid={`edit-data-table-row-${index}`}
      onDragOver={onDragOver(index)}
      onDragStart={onDragStart(index)}
      onDrop={onDrop(index)}
      draggable={hasDragDrop}
      className={`data-table-row ${
        index >= rowsCount ? 'outOfLimit' : ''
      } ${editRowIndex !== -1 &&
        editRowIndex - index !== 0 &&
        'lockTheRow'} ${editRowIndex === index && 'editable-row'}`}
    >
      <div className="drag-and-checkbox">
        {hasDragDrop && (
          <DragIndicator
            style={{ color: '#757575' }}
            disabled={editRowIndex === index}
          />
        )}
        {hasSelect && (
          <Checkbox
            checked={selectedRows?.includes(index)}
            onChange={handleChange}
            color="primary"
            inputProps={{
              'aria-label': 'secondary checkbox',
              'data-testid': `data-table-row-${index}`
            }}
            value={index}
            disabled={editRowIndex === index}
          />
        )}
      </div>
      {dataAttributes.map((key, idx) => {
        let rowElement = ''
        rowElement = (
          <React.Fragment key={idx}>
            {hasEdit && editRowIndex === index ? (
              getEditableRow(key)
            ) : (
              <div key={idx} className="data-text">
                {formatNumber(key, localRow[key])}
              </div>
            )}
          </React.Fragment>
        )

        return rowElement
      })}
      {hasEdit && editRowIndex !== index && (
        <Edit
          data-testid={`btn-edit-${index}`}
          className="btn-action right60"
          onClick={() => handleSetEdit(index)}
        />
      )}
      {hasDelete && editRowIndex !== index && (
        <Delete
          data-testid={`btn-delete-${index}`}
          className={minimumOneRow && isLastRow ? 'btn-disabled' : 'btn-action'}
          onClick={() => onDelete(index)}
        />
      )}
      {hasEdit && editRowIndex === index && (
        <>
          <Done
            data-testid={`btn-done-${index}`}
            className="btn-action right60"
            onClick={() => handleUpdate()}
          />
          <Block
            data-testid={`btn-block-${index}`}
            className="btn-action"
            onClick={() => handleBlock()}
          />
        </>
      )}
    </div>
  )
}

DataTableRow.propTypes = {
  editRowIndex: PropTypes.number,
  onSelect: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragStart: PropTypes.func,
  onDrop: PropTypes.func,
  row: PropTypes.object,
  index: PropTypes.number,
  rowsCount: PropTypes.number,
  dataAttributes: PropTypes.array,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onUpdate: PropTypes.func,
  isLastRow: PropTypes.bool,
  selectedRows: PropTypes.array,
  minimumOneRow: PropTypes.bool,
  actionItems: PropTypes.shape({
    hasEdit: PropTypes.bool,
    hasDelete: PropTypes.bool,
    hasDragDrop: PropTypes.bool,
    hasSelect: PropTypes.bool
  }),
  newRowFlag: PropTypes.bool,
  confirmAddRow: PropTypes.func,
  onEmptyRow: PropTypes.func,
  cancelAddRow: PropTypes.func
}

export default DataTableRow
